import React, { useCallback, useEffect, useRef } from "react";
import { StdCountUp } from "../StdCountUp";
import { Figure } from "../Figure";
import { PROVIDER_CONNECTION_TYPE, FilterMetadataApiWithCountReturn, WaterProvider } from "../misc";
import ReactResizeDetector from "react-resize-detector";
import { Paragraph } from "../Paragraph";
import { LoadingIndicator } from "../LoadingIndicator";
import _ from "lodash";
import styles from "./ProviderInfoContent.module.scss";
import { useUpdateHeights, useBreakPoints } from "../../Components/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLessThan } from "@fortawesome/pro-solid-svg-icons/faLessThan";
import { faGreaterThanEqual } from "@fortawesome/pro-solid-svg-icons/faGreaterThanEqual";

interface LProviderInfoContentProps {
    provider?: WaterProvider;
    metadata?: FilterMetadataApiWithCountReturn;
    loading?: boolean;
    className?: string;
    enabledHeightWatching?: boolean;
}

export const HEIGHT_WATCHING_LINE_1 = "height_watching_line_1";
export const HEIGHT_UPDATE_LINE_1 = "height_update_line_1";

export const HEIGHT_WATCHING_HEADLINE = "height_watching_headline";
export const HEIGHT_UPDATE_HEADLINE = "height_update_headline";

const breakPoints = [
    {
        class: "xsmall",
        value: 300
    },
    {
        class: "small",
        value: 500
    },
    {
        class: "xmedium",
        value: 640
    },
    {
        class: "medium",
        value: 900
    },
    {
        class: "large",
        value: 1500
    },
    {
        class: "xlarge",
        value: null
    }
];

const BULK_PROVIDER_TYPE_LABEL = "Bulk water provider";

const LProviderInfoContent: React.FC<LProviderInfoContentProps> = ({
    provider,
    metadata,
    loading,
    className,
    enabledHeightWatching
}) => {
    const ref = useRef();

    const updateHeightsForLine1 = useUpdateHeights(
        enabledHeightWatching,
        ref,
        `.${HEIGHT_WATCHING_LINE_1}`,
        `.${HEIGHT_UPDATE_LINE_1}`
    );

    const updateHeightsForHeadline = useUpdateHeights(
        enabledHeightWatching,
        ref,
        `.${HEIGHT_WATCHING_HEADLINE}`,
        `.${HEIGHT_UPDATE_HEADLINE}`
    );

    const breakResolver = useBreakPoints(breakPoints, ref);

    useEffect(() => {
        enabledHeightWatching && updateHeightsForLine1();
        enabledHeightWatching && updateHeightsForHeadline();
    }, [provider, metadata, enabledHeightWatching, updateHeightsForLine1, updateHeightsForHeadline]);

    const onResizeContentInfo = useCallback(() => {
        breakResolver();
        enabledHeightWatching === true && updateHeightsForLine1();
        enabledHeightWatching === true && updateHeightsForHeadline();
    }, [enabledHeightWatching, breakResolver, updateHeightsForLine1, updateHeightsForHeadline]);

    if (!metadata)
        return (
            <div className="col-12">
                <LoadingIndicator />
            </div>
        );

    if (!provider && loading)
        return (
            <div className="col-12">
                <LoadingIndicator />
            </div>
        );

    if (!provider) return <div className="col-12">Cannot find the provider</div>;

    const { population, connectionSizeCategory, type, region, area, damNum, damCapacity, employeeNum, description } =
        provider;

    const providerSizeOptions = metadata?.["providerSize"]?.options ?? [];
    const sizeKeyToProviderSizeOption = _.keyBy(providerSizeOptions, d => d.value);

    const providerRegionOptions = metadata?.["providerRegion"]?.options ?? [];
    const regionKeyToProviderRegionOption = _.keyBy(providerRegionOptions, d => d.value);

    // console.log(metadata);
    const providerTypeOptions = metadata?.["providerType"]?.options ?? [];
    const providerTypeKeyToProviderTypeOption = _.keyBy(providerTypeOptions, d => d.value);

    const size = sizeKeyToProviderSizeOption?.[connectionSizeCategory];

    return (
        <div className={`col-12 ${className ?? ""} ${styles.providerInfo}`} ref={ref}>
            <div className={`row ${styles.rowJustify} ${styles.twoColumnWithTitle} ${HEIGHT_UPDATE_HEADLINE}`}>
                {loading === true && (
                    <div className="col-12">
                        <LoadingIndicator />
                    </div>
                )}
                <div className={`text-center ${styles.population}`}>
                    <Figure
                        header={"Local government area population"}
                        value={population}
                        className={`${HEIGHT_WATCHING_HEADLINE}`}
                    ></Figure>
                </div>
                <div className={`text-center ${styles.connectionSize}`}>
                    {size == null && connectionSizeCategory === PROVIDER_CONNECTION_TYPE.bulk && (
                        <Figure className={HEIGHT_WATCHING_HEADLINE} header={"Connection size"} value={"Bulk water"} />
                    )}
                    {size != null && (
                        <Figure
                            className={HEIGHT_WATCHING_HEADLINE}
                            header={"Connection size"}
                            value={size?.label ?? ""}
                            footer={
                                <>
                                    {size?.queryRangeStart != null && size?.queryRangeEnd != null && (
                                        <>
                                            <StdCountUp value={size?.queryRangeStart} />
                                            {" - "}
                                            <StdCountUp value={size?.queryRangeEnd} />
                                        </>
                                    )}

                                    {size?.queryRangeStart != null && size?.queryRangeEnd == null && (
                                        <>
                                            <FontAwesomeIcon icon={faGreaterThanEqual} className={styles.mathSign} />{" "}
                                            <StdCountUp value={size?.queryRangeStart} />
                                        </>
                                    )}

                                    {size?.queryRangeStart == null && size?.queryRangeEnd != null && (
                                        <>
                                            <FontAwesomeIcon icon={faLessThan} className={styles.mathSign} />{" "}
                                            <StdCountUp value={size?.queryRangeEnd} />
                                        </>
                                    )}
                                </>
                            }
                        ></Figure>
                    )}
                </div>
            </div>

            <div
                className={`row ${styles.rowJustify} ${styles.highLineHeight} ${styles.threeColumn} ${HEIGHT_UPDATE_LINE_1}`}
            >
                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>Type: </span>
                        <span>
                            {connectionSizeCategory === PROVIDER_CONNECTION_TYPE.bulk
                                ? BULK_PROVIDER_TYPE_LABEL
                                : providerTypeKeyToProviderTypeOption?.[type]?.label ?? ""}
                        </span>
                    </div>
                </div>

                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>Region: </span>
                        <span>{regionKeyToProviderRegionOption?.[region]?.label ?? ""}</span>
                    </div>
                </div>

                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>Area: </span>
                        <span>
                            <StdCountUp value={area} /> km2
                        </span>
                    </div>
                </div>
            </div>

            <div
                className={`row ${styles.rowJustify} ${styles.highLineHeight} ${styles.twoColumn} ${HEIGHT_UPDATE_LINE_1}`}
            >
                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>Number of dams: </span>
                        <span>
                            <StdCountUp value={damNum} />
                        </span>
                    </div>
                </div>

                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>Capacity of dams (ML): </span>
                        <span>
                            <StdCountUp value={damCapacity} />
                        </span>
                    </div>
                </div>
            </div>

            <div className={`row ${styles.rowJustify} ${styles.highLineHeight} ${HEIGHT_UPDATE_LINE_1}`}>
                <div className={`${styles.heightWatchingContainer}`}>
                    <div
                        className={`default-margin-bottom ${HEIGHT_WATCHING_LINE_1} ${styles.heightWatching} ${styles.typeValue}`}
                    >
                        <span>No. Full-time equivalent water and sewerage employees: </span>
                        <span>
                            <StdCountUp value={employeeNum} />
                        </span>
                    </div>
                </div>
            </div>

            <div className={`row ${styles.rowJustify} ${HEIGHT_UPDATE_LINE_1}`}>
                <span className={`col-12 default-margin-bottom ${styles.typeValue} ${styles.aboutThisProvider}`}>
                    About this provider
                </span>
                <div className="col-12 default-margin-bottom">
                    <Paragraph
                        title=""
                        nid={1}
                        status={true}
                        text={{
                            value: description,
                            processed: description
                        }}
                    />
                </div>
            </div>

            <ReactResizeDetector handleWidth={true} handleHeight={true} onResize={onResizeContentInfo} />
        </div>
    );
};

export const ProviderInfoContent = React.memo(LProviderInfoContent, (prev, next) => {
    return (
        prev?.provider === next?.provider &&
        prev.metadata === next.metadata &&
        prev.loading === next.loading &&
        prev.className === next.className
    );
});
